import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  Renderer2,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { NgClassInterface, SafeAny } from '@ngx-simple/core/types';
import { take } from 'rxjs/operators';

/** Function that can generate the extra classes that should be added to a calendar cell. */
export type SimCalendarCellClassFunction<D> = (date: D, view: 'month' | 'year' | 'multi-year') => NgClassInterface;

/** 表示与单个日历单元格对应的数据的内部类 */
export class CalendarCell<D = SafeAny> {
  constructor(
    public value: number,
    public displayValue: string,
    public ariaLabel: string,
    public enabled: boolean,
    public cssClasses: NgClassInterface = {},
    public compareValue = value,
    public rawValue?: D
  ) {}
}

/** 当用户操作触发日历中的日期时触发的事件 */
export interface CalendarUserEvent<D> {
  value: D;
  event: Event;
  cell?: CalendarCell;
}

@Component({
  selector: 'tbody[sim-calendar-body]',
  templateUrl: './calendar-body.component.html',
  styleUrls: ['./datepicker.component.scss'],
  exportAs: 'simCalendarBody',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sim-calendar-body'
  }
})
export class SimCalendarBodyComponent<D> implements OnChanges, OnDestroy {
  @Input() activeMonth: number;

  /** 要在表格中显示的单元格 */
  @Input() rows: CalendarCell[][];

  /** 表中的列数 */
  @Input() numCols: number = 7;

  /** 所选日期范围的起始值 */
  @Input() startValue: number = 7;

  /** 选定日期范围的结束值 */
  @Input() endValue: number = 7;

  /** 表中活动单元格的单元格编号 */
  @Input() activeCell: number = 7;

  /** 比较范围的开始 */
  @Input() comparisonStart: number | null;

  /** 比较范围的结尾 */
  @Input() comparisonEnd: number | null;

  /** 预览范围的开始 */
  @Input() previewStart: number | null = null;

  /** 预览范围的结尾 */
  @Input() previewEnd: number | null = null;

  /** 表中对应于今天的值 */
  @Input() todayValue: number;

  /** 是否选择范围 */
  @Input() isRange: boolean;

  /** 表格中单元格所使用的宽高比（宽度/高度）。 即使在表调整大小时，也将保持该长宽比。 */
  @Input() cellAspectRatio: number = 1;

  /** 选择新值时发出 */
  @Output() readonly selectedValueChange: EventEmitter<CalendarUserEvent<number>> = new EventEmitter<CalendarUserEvent<number>>();

  /** 当预览由于用户操作而更改时发出 */
  @Output() previewChange = new EventEmitter<CalendarUserEvent<CalendarCell | null>>();

  /** 单个单元格的宽度 */
  _cellWidth: string;

  /** 单个日期单元格的填充 */
  _cellPadding: string;

  /**
   * 用于在渲染预览范围时跳过下一个焦点事件。
   * 我们需要这样的标记，因为某些浏览器会异步触发焦点事件。
   */
  private _skipNextFocus: boolean;

  constructor(
    private _elementRef: ElementRef,
    private _changeDetectorRef: ChangeDetectorRef,
    private _renderer: Renderer2,
    private _ngZone: NgZone,
    @Optional() private _dateAdapter: SimDateAdapter<D>
  ) {
    _ngZone.runOutsideAngular(() => {
      const element = _elementRef.nativeElement;
      element.addEventListener('mouseenter', this._enterHandler, true);
      element.addEventListener('focus', this._enterHandler, true);
      element.addEventListener('mouseleave', this._leaveHandler, true);
      element.addEventListener('blur', this._leaveHandler, true);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const columnChanges = changes['numCols'];

    const { numCols } = this;

    if (changes['numCols'] || !this._cellWidth) {
      this._cellWidth = `${100 / numCols}%`;
    }

    if (changes['cellAspectRatio'] || columnChanges || !this._cellPadding) {
      this._cellPadding = `${(50 * this.cellAspectRatio) / numCols}%`;
    }
  }

  ngOnDestroy() {
    const element = this._elementRef.nativeElement;
    element.removeEventListener('mouseenter', this._enterHandler, true);
    element.removeEventListener('focus', this._enterHandler, true);
    element.removeEventListener('mouseleave', this._leaveHandler, true);
    element.removeEventListener('blur', this._leaveHandler, true);
  }

  /** 在微任务队列为空之后，对活动单元进行聚焦 */
  _focusActiveCell(movePreview = true) {
    this._ngZone.runOutsideAngular(() => {
      this._ngZone.onStable
        .asObservable()
        .pipe(take(1))
        .subscribe(() => {
          const activeCell: HTMLElement | null = this._elementRef.nativeElement.querySelector('.sim-calendar-body-active');
          if (activeCell) {
            if (!movePreview) {
              this._skipNextFocus = true;
            }
            activeCell.focus();
          }
        });
    });
  }

  /** 本月 */
  _isCurrentMonth(cell: CalendarCell): boolean {
    if (this.activeMonth == null) {
      return false;
    }
    return this.activeMonth === this._dateAdapter.getMonth(cell.rawValue);
  }
  /** 上一个月 */
  _isPreviousMonth(cell: CalendarCell): boolean {
    if (this.activeMonth == null) {
      return false;
    }
    return this._dateAdapter.getMonth(cell.rawValue) < this.activeMonth;
  }
  /** 下一个月 */
  _isNextMonth(cell: CalendarCell): boolean {
    if (this.activeMonth == null) {
      return false;
    }
    return this._dateAdapter.getMonth(cell.rawValue) > this.activeMonth;
  }

  _isSelected(cell: CalendarCell) {
    const isSelected = this.startValue === cell.compareValue || this.endValue === cell.compareValue;
    if (this.activeMonth == null) {
      return isSelected;
    }
    return this._isCurrentMonth(cell) && isSelected;
  }

  _isActiveCell(rowIndex: number, colIndex: number): boolean {
    const cellNumber = rowIndex * this.numCols + colIndex;
    return cellNumber === this.activeCell;
  }

  _cellClicked(cell: CalendarCell, event: MouseEvent) {
    if (cell.enabled) {
      this.selectedValueChange.emit({ value: cell.value, event, cell });
    }
  }

  _isRangeStart(value: number) {
    return isStart(value, this.startValue, this.endValue);
  }

  /** 获取值是否是主范围的结束 */
  _isRangeEnd(value: number) {
    return isEnd(value, this.startValue, this.endValue);
  }

  /** 获取值是否在当前选择的范围内 */
  _isInRange(value: number): boolean {
    return isInRange(value, this.startValue, this.endValue, this.isRange);
  }

  /** 获取值是否是比较范围的开始 */
  _isComparisonStart(value: number) {
    return isStart(value, this.comparisonStart, this.comparisonEnd);
  }

  /** 获取值是否是比较范围的结束 */
  _isComparisonEnd(value: number) {
    return isEnd(value, this.comparisonStart, this.comparisonEnd);
  }

  /** 获取值是否在当前比较范围内 */
  _isInComparisonRange(value: number) {
    return isInRange(value, this.comparisonStart, this.comparisonEnd, this.isRange);
  }

  _isComparisonIdentical(value: number) {
    // 注意，我们不需要对开始/结束进行空检查，因为这里将始终定义`value`。
    return this.comparisonStart === this.comparisonEnd && value === this.comparisonStart;
  }

  /** 获取值是否是预览范围的开始 */
  _isPreviewStart(value: number) {
    return isStart(value, this.previewStart, this.previewEnd);
  }

  /** 获取值是否是预览范围的结束 */
  _isPreviewEnd(value: number) {
    return isEnd(value, this.previewStart, this.previewEnd);
  }

  /** 获取值是否在预览范围内 */
  _isInPreview(value: number) {
    return isInRange(value, this.previewStart, this.previewEnd, this.isRange);
  }

  /** 该单元格是否是主范围和比较范围之间的起始桥单元  */
  _isComparisonBridgeStart(value: number, rowIndex: number, colIndex: number) {
    if (!this._isComparisonStart(value) || this._isRangeStart(value) || !this._isInRange(value)) {
      return false;
    }

    let previousCell: CalendarCell | undefined = this.rows[rowIndex][colIndex - 1];

    if (!previousCell) {
      const previousRow = this.rows[rowIndex - 1];
      previousCell = previousRow && previousRow[previousRow.length - 1];
    }

    return previousCell && !this._isRangeEnd(previousCell.compareValue);
  }

  /** 单元格是否是主范围和比较范围之间的末端桥单元 */
  _isComparisonBridgeEnd(value: number, rowIndex: number, colIndex: number) {
    if (!this._isComparisonEnd(value) || this._isRangeEnd(value) || !this._isInRange(value)) {
      return false;
    }

    let nextCell: CalendarCell | undefined = this.rows[rowIndex][colIndex + 1];

    if (!nextCell) {
      const nextRow = this.rows[rowIndex + 1];
      nextCell = nextRow && nextRow[0];
    }

    return nextCell && !this._isRangeStart(nextCell.compareValue);
  }

  /**
   * 当用户在日历正文中输入一个元素时的事件处理程序(例如，通过将鼠标悬停或聚焦)。
   */
  private _enterHandler = (event: Event) => {
    if (this._skipNextFocus && event.type === 'focus') {
      this._skipNextFocus = false;
      return;
    }

    // 我们只需要在选择范围时点击区域即可
    if (event.target && this.isRange) {
      const cell = this._getCellFromElement(event.target as HTMLElement);

      if (cell) {
        this._ngZone.run(() => this.previewChange.emit({ value: cell.enabled ? cell : null, event }));
      }
    }
  };

  /**
   * 事件处理程序，当用户的指针在日历正文中留下一个元素时(例如，悬停或失焦)。
   */
  private _leaveHandler = (event: Event) => {
    // 我们只需要在选择范围时点击区域即可
    if (this.previewEnd !== null && this.isRange) {
      // 仅在离开单元格时重置预览结束值。
      // 这看起来更好，因为我们在单元格和行之间留有间隙，
      // 并且我们不想删除该范围，只是为了在用户向侧面移动几个像素时再次显示该范围。
      if (event.target && isTableCell(event.target as HTMLElement)) {
        this._ngZone.run(() => this.previewChange.emit({ value: null, event }));
      }
    }
  };

  /** 查找与DOM节点相对应的CalendarCell */
  private _getCellFromElement(element: HTMLElement): CalendarCell | null {
    let cell: HTMLElement | undefined;

    if (isTableCell(element)) {
      cell = element;
    } else if (isTableCell(element.parentNode)) {
      cell = element.parentNode as HTMLElement;
    }

    if (cell) {
      const row = cell.getAttribute('data-sim-row');
      const col = cell.getAttribute('data-sim-col');

      if (row && col) {
        return this.rows[parseInt(row, 10)][parseInt(col, 10)];
      }
    }

    return null;
  }
}

/** 检查节点是否为表格单元元素 */
function isTableCell(node: Node): node is HTMLTableCellElement {
  return node.nodeName === 'TD';
}

/** 检查一个值是否为范围的开始 */
function isStart(value: number, start: number | null, end: number | null): boolean {
  return end !== null && start !== end && value < end && value === start;
}

/** 检查一个值是否为范围的结束 */
function isEnd(value: number, start: number | null, end: number | null): boolean {
  return start !== null && start !== end && value >= start && value === end;
}

/** 检查值是否在范围内 */
function isInRange(value: number, start: number | null, end: number | null, rangeEnabled: boolean): boolean {
  return rangeEnabled && start !== null && end !== null && start !== end && value >= start && value <= end;
}
